import { LEAVES_BG_XS, LEAVES_BG_MD } from '../../config/backgrounds';
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Search, CalendarToday, Article as ArticleIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import blogPostService from '../../services/blogPostService';

const BlogListPage = () => {
  const theme = useTheme();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(9);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBlogPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogPostService.getBlogPosts({
        page,
        limit,
        search: searchTerm,
        isPublished: true,
      });
      if (response.success) {
        setBlogPosts(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.message || 'Blogbejegyzések betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Hiba történt a blogbejegyzések betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchBlogPosts();
  }, [fetchBlogPosts]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const pageBackground = {
    minHeight: '100vh',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundImage: {
        xs: LEAVES_BG_XS,
        md: LEAVES_BG_MD,
      },
      backgroundSize: { xs: '100% auto', md: 'cover' },
      backgroundPosition: { xs: 'center top', md: 'center center' },
      backgroundRepeat: 'no-repeat',
      zIndex: -1,
      willChange: 'transform',
      transform: 'translateZ(0)',
    },
  };

  if (loading) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="lg" sx={{ pt: 8, textAlign: 'center' }}>
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2, color: 'primary.dark' }}>Betöltés...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageBackground}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Esküvői tippek és ötletek – Blog | Wedigo</title>
        <meta name="description" content="Hasznos tippek, ötletek és inspiráció esküvőtervezéshez. Olvass cikkeket fotósokról, dekorációról, helyszínekről és minden másról, ami az esküvődhöz kell." />
        <meta property="og:title" content="Esküvői tippek és ötletek – Blog | Wedigo" />
        <meta property="og:description" content="Hasznos tippek, ötletek és inspiráció esküvőtervezéshez." />
        <meta property="og:url" content="https://wedigo.hu/blog" />
        <link rel="canonical" href="https://wedigo.hu/blog" />
      </Helmet>

      <Box sx={pageBackground}>
        <Container maxWidth="lg" sx={{ pt: 3, pb: 6 }}>

          {/* Hero fejléc */}
          <Paper elevation={0} sx={{
            mb: 4,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: '20px',
            background: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: 'blur(12px)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.30)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.10)}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ArticleIcon sx={{ color: '#fff', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: 'primary.dark', lineHeight: 1.2 }}>
                  Blog
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tippek, trendek és inspiráció esküvőtervezéshez
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Keresés a blogban"
              variant="outlined"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                width: { xs: '100%', sm: 320 },
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              }}
            />
          </Paper>

          {/* Blog kártyák */}
          {blogPosts.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              Jelenleg nincsenek publikált blogbejegyzések.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {blogPosts.map((post) => (
                <Card key={post.id} sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  borderRadius: '20px',
                  border: `1px solid ${theme.palette.divider}`,
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.16)',
                    borderColor: theme.palette.secondary.main,
                  },
                }}>
                  {post.imageUrl ? (
                    <CardMedia
                      component="img"
                      image={post.imageUrl}
                      alt={post.title}
                      sx={{
                        width: { xs: '100%', md: 300 },
                        height: { xs: 220, md: 'auto' },
                        flexShrink: 0,
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box sx={{
                      width: { xs: '100%', md: 300 },
                      height: { xs: 160, md: 'auto' },
                      flexShrink: 0,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <ArticleIcon sx={{ fontSize: 56, color: alpha(theme.palette.primary.main, 0.25) }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3, '&:last-child': { pb: 3 } }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.dark', lineHeight: 1.3 }}>
                      {post.title}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: 'text.secondary', gap: 0.5 }}>
                      <CalendarToday fontSize="small" />
                      <Typography variant="caption">
                        {format(parseISO(post.publishedAt), 'yyyy. MM. dd.', { locale: hu })}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                      {post.metaDescription || post.content.substring(0, 200) + '...'}
                    </Typography>

                    <Box>
                      <Button
                        component={Link}
                        to={`/blog/${post.slug}`}
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: '10px' }}
                      >
                        Tovább olvasom
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': { borderRadius: '10px' },
                }}
              />
            </Box>
          )}

        </Container>
      </Box>
    </>
  );
};

export default BlogListPage;
